import { Capacitor } from "@capacitor/core";
import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Box, Button, Divider, Drawer, IconButton, Toolbar, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type React from "react";
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { type Department, useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useIsMobile } from "../../hooks/useMobile";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRevealWageActions } from "../../hooks/useRevealWage";
import { useThemePreference } from "../../theme/ThemePreferenceProvider";
import { ALPHA, LOGO_OPTION_BY_ID, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { AppPlatform } from "../../utils/models";
import { isBedSideNurse, isHeadNurse } from "../../utils/utilFunctions";
import { NotificationBell } from "../notificationBell";
import { useIsGapsViewActive } from "../staffing/mobileGaps/useGapsViewPreference";
import { UserSwitcher } from "../UserSwitcher";
import AdminDrawer from "./AdminDrawer";
import { DepartmentSwitcher } from "./DepartmentSwitcher";
import { usePerformGlobalRefresh, usePollRefreshStatus, useRegisterAutoFetchSystemData } from "./hooks";
import { LanguageIconButton } from "./LanguageIconButton";
import MobileDrawerOptions from "./MobileDrawerOptions";
import { RefreshIconButton } from "./RefreshIconButton";
import { appBarSx, getRefreshOptionsByGroupedActivityLog, headerStyles, toolbarSx } from "./utils";
import { WageRevealButton } from "./WageRevealButton";

type Props = {
	/** The highlighted tab's index in `tabs`; -1 (none of them owns the route) highlights none. */
	activeTab: number;
	setActiveTab: (index: number) => void;
	tabs: string[];
	enabledTabs: string[];
	departments: Department[];
	setDepartmentListener: (department: Department) => Promise<void>;
	/** Opens full-screen Annie from the head-nurse menu. Undefined when Annie isn't available (desktop / flag off). */
	onOpenAnnie?: () => void;
};

export const Header = ({ activeTab, setActiveTab, tabs, enabledTabs, departments, setDepartmentListener, onOpenAnnie }: Props) => {
	const { chosenDepartment } = useChosenDepartment();
	const { refreshRequests } = useRequestsContext();
	const { fetchNotifications } = useNotificationsContext();
	const navigate = useNavigate();
	const { isMobile } = useIsMobile();
	const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
	const { userInfo } = useAuth();
	const [nurseImage, setNurseImage] = useState<string | null>(null);
	// The gaps-first bar owns navigation while that view is on, including the menu this hamburger opens.
	const isGapsViewActive = useIsGapsViewActive();
	const { t } = useTranslation();
	const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
	const location = useLocation();
	const [isBedSide, setIsBedSide] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const { logo } = useThemePreference();
	const activeLogo = LOGO_OPTION_BY_ID[logo];

	const { logout } = useAuth();
	const { trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const isManualRefreshButtonFunctionalityEnabled = useMemo(() => isHeadNurse(userInfo.role), [userInfo.role]);

	const nurseDepartments = useMemo(() => {
		if (!userInfo?.nurses_info || !userInfo?.departments) return [];

		// Get the set of department IDs from nurse info
		const nurseDepartmentIds = new Set(userInfo.nurses_info.flatMap((nurseInfo) => nurseInfo.departments));

		// Find the matching Department objects from userInfo.departments to get display names
		return userInfo.departments.filter((dept) => nurseDepartmentIds.has(dept.department_id));
	}, [userInfo?.nurses_info, userInfo?.departments]);

	useEffect(() => {
		setIsBedSide(isBedSideNurse(userInfo.role));
	}, [userInfo]);

	useEffect(() => {
		refreshRequests();
		fetchNotifications();
	}, [chosenDepartment.department_id]);

	const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
		setActiveTab(index);
	};

	const handleDepartmentChange = useCallback(
		async (department: Department): Promise<void> => {
			try {
				await setDepartmentListener(department);
			} catch (err) {
				trackError("department_switch_error", { error_message: (err as Error)?.message });
			}
		},
		[setDepartmentListener, trackError],
	);

	const handleAdminMenuClick = (_: React.MouseEvent<HTMLElement>) => {
		setIsAdminMenuOpen((prevState) => !prevState);
		trackEvent("profile image menu clicked");
	};

	useEffect(() => {
		setNurseImage(userInfo?.image);
	}, [userInfo]);

	const handleNotificationsClose = () => {
		setNotificationsAnchorEl(null);
	};

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const openNotifications = searchParams.get("openNotifications");

		if (openNotifications === "true" && notificationsAnchorEl) {
			handleNotificationsClose();
		}
	}, [location.search, notificationsAnchorEl]);

	const handleDrawerToggle = () => {
		setDrawerOpen(!drawerOpen);
	};

	const handleLogout = async () => {
		trackEvent("logout clicked");
		try {
			await logout();
			if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === AppPlatform.Android) return;
			window.location.reload();
		} catch (error) {
			console.error("Logout failed:", error);
			showSnackbar(t("error.general"), "error");
		}
	};

	const getPageTitle = () => {
		if (!isBedSide) return null;
		if (location.pathname.split("/").length > 1) {
			if (location.pathname.split("/")[1] === "day-schedule") {
				return t("day.schedule");
			}
		}

		switch (location.pathname) {
			case "/my-schedule":
				return t("schedule");
			case "/open-shifts":
				return t("open.shifts");
			case "/preferences":
				return t("work.preferences");
			case "/staffing":
				return t("staffing");
			case "/pick-shift":
				return t("pick.shift");
			case "/post-shift":
				return t("post.shift");
			case "/notifications":
				return t("notifications");
			default:
				return null;
		}
	};
	const { data: refreshStatusData, refetch: refetchRefreshStatus } = usePollRefreshStatus({
		disableHook: !isManualRefreshButtonFunctionalityEnabled,
	});
	const areThereAnyUpdates = useMemo(() => {
		return (
			(refreshStatusData &&
				(refreshStatusData.requests.changeCount > 0 ||
					refreshStatusData.nurses.changeCount > 0 ||
					refreshStatusData.departmentData.changeCount > 0 ||
					refreshStatusData.notifications.changeCount > 0 ||
					refreshStatusData.wage.changeCount > 0)) ??
			false
		);
	}, [refreshStatusData]);
	const { performGlobalRefresh, isLoading: isRefreshingNow } = usePerformGlobalRefresh({ refetchRefreshStatus });
	const { isLoading: isAutoFetchRefreshingNow } = useRegisterAutoFetchSystemData({
		refetchRefreshStatus,
		lastFetchedRefreshStatusData: refreshStatusData,
	});

	const handlePerformGlobalRefresh = useCallback(async () => {
		const refreshOptions = refreshStatusData ? getRefreshOptionsByGroupedActivityLog(refreshStatusData) : undefined;
		await performGlobalRefresh(refreshOptions);
	}, [performGlobalRefresh, refreshStatusData]);

	const isShiftInsightsEnabled = useFeatureFlag(FEATURE_FLAGS.ShiftInsightsTab);
	const handleLogoClick = useCallback(() => {
		if (isBedSide) {
			navigate("/my-schedule");
		} else if (chosenDepartment?.department_id) {
			// Match the redirect logic from App.tsx
			if (isShiftInsightsEnabled) {
				navigate(`/${chosenDepartment.department_id}/shift-insights`);
			} else {
				navigate(`/${chosenDepartment.department_id}/staffing`);
			}
		} else {
			navigate("/");
		}
	}, [isBedSide, chosenDepartment?.department_id, userInfo.role, navigate]);

	const { viewWageAllowed, revealWage } = useRevealWageActions();
	// The strip is the header-wide echo of the wage toggle, so it has to be gated on exactly what puts
	// that toggle on screen — otherwise a role without the control gets a reminder it can't act on.
	const isWageHidden = isHeadNurse(userInfo.role) && viewWageAllowed && !revealWage;

	return (
		<AppBar position={Capacitor.isNativePlatform() ? "fixed" : "static"} sx={appBarSx}>
			<Toolbar sx={toolbarSx(isWageHidden)}>
				<Box onClick={handleLogoClick} style={headerStyles.logo as CSSProperties}>
					<Box sx={headerStyles.logoImgContainer}>
						<img alt="Logo" src={isBedSide ? activeLogo.bedside : activeLogo.header} style={headerStyles.logoImg as CSSProperties} />
					</Box>
				</Box>
				{isBedSide && getPageTitle() && (
					<Typography variant="h6" sx={headerStyles.pageTitle}>
						{getPageTitle()}
					</Typography>
				)}
				<Divider orientation="horizontal" sx={headerStyles.divider} />

				{!isMobile &&
					tabs.map((headerText, index) => {
						return (
							<Button
								key={headerText}
								data-testid={`header-tab-${headerText}`}
								// Only the first tab: an anchor has to be a fixed value, and the per-tab test id is a
								// template literal.
								data-annie-target={index === 0 ? "main-nav-first-tab" : undefined}
								color="inherit"
								onClick={() => handleMenuItemClick({} as React.MouseEvent<HTMLElement>, index)}
								sx={{
									...headerStyles.tabButton,
									color: activeTab === index ? SURFACE : alpha(SURFACE, 1 - ALPHA.SHADOW_HEAVY),
								}}
								disabled={!enabledTabs.includes(headerText)}
							>
								{t(headerText)}
							</Button>
						);
					})}

				<Box sx={{ flexGrow: 1 }} />
				<Box sx={headerStyles.actionGroup}>
					{/* In the gaps-first view this control lives in the More menu instead — one home per action. */}
					{isHeadNurse(userInfo.role) && !isGapsViewActive && <WageRevealButton />}
					{isManualRefreshButtonFunctionalityEnabled && (
						<RefreshIconButton
							onClick={handlePerformGlobalRefresh}
							areThereAnyUpdates={areThereAnyUpdates}
							isRefreshingNow={isRefreshingNow || isAutoFetchRefreshingNow}
						/>
					)}
					{isBedSide ? <LanguageIconButton color="inherit" size="small" /> : null}
					<NotificationBell color="inherit" size="small" />

					{(userInfo?.support || userInfo?.impersonated_by) && <UserSwitcher />}
					{!isBedSide && <DepartmentSwitcher departments={departments} onChange={handleDepartmentChange} />}
					{isBedSide && (location.pathname.includes("day-schedule") || location.pathname.includes("preferences")) && nurseDepartments.length > 1 && (
						<DepartmentSwitcher departments={nurseDepartments} onChange={handleDepartmentChange} isBedSide />
					)}
					{isBedSide ? (
						<>
							<IconButton data-testid="header-mobile-menu" onClick={handleDrawerToggle} sx={headerStyles.menuButton}>
								<MenuIcon />
							</IconButton>
							<Drawer anchor="right" open={drawerOpen} onClose={handleDrawerToggle} sx={headerStyles.drawer}>
								<Box sx={headerStyles.drawerContent}>
									<MobileDrawerOptions handleDrawerToggle={handleDrawerToggle} headerStyles={headerStyles} handleLogout={handleLogout} navigate={navigate} />
								</Box>
							</Drawer>
						</>
					) : (
						// The gaps-first bottom bar's "More" slot opens this same menu, so the hamburger would be a
						// second door to one room. It goes; the bar is the way in for as long as that view is on.
						!isGapsViewActive && (
							<IconButton data-testid="header-admin-menu" data-annie-target="admin-menu-button" onClick={handleAdminMenuClick} sx={headerStyles.menuButton}>
								<MenuIcon />
							</IconButton>
						)
					)}
				</Box>
			</Toolbar>
			{!isBedSide && (
				<AdminDrawer setMenuOpen={setIsAdminMenuOpen} userInfo={userInfo} open={isAdminMenuOpen} profileImage={nurseImage} onOpenAnnie={onOpenAnnie} />
			)}
		</AppBar>
	);
};
